import PageHeader from "@/components/PageHeader";
import { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import StatusBadge from "@/components/StatusBadge";
import {
  Plus, ClipboardCheck, TrendingUp, ShieldCheck, AlertCircle, Loader2,
  CheckCircle2, XCircle, FileText, Target as TargetIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const MAX = {
  production_score: 4,
  quality_score: 4,
  attendance_score: 0.5,
  discipline_score: 0.5,
  assessment_score: 1,
};

const PROJECT_OPTIONS = ["SH - Home Health"];
const CLIENT_OPTIONS = ["Symmetry", "Wellsky"];
const PROCESS_OPTIONS = ["Coding", "QA"];

const todayStr = () => new Date().toISOString().slice(0, 10);
const monthStr = () => new Date().toISOString().slice(0, 7);

// Performance Rating matrix — kept in sync with backend _rating_from_score
const RATING_MATRIX = [
  { code: "EE", label: "Exceeds Expectations", min: 9.75, color: "emerald", border: "border-emerald-300", bg: "bg-emerald-50", text: "text-emerald-700" },
  { code: "AE", label: "Above Expectations", min: 9.50, color: "blue", border: "border-blue-300", bg: "bg-blue-50", text: "text-blue-700" },
  { code: "CME", label: "Consistently Meets Expectations", min: 9.25, color: "amber", border: "border-amber-300", bg: "bg-amber-50", text: "text-amber-700" },
  { code: "NI", label: "Needs Improvement", min: 0, color: "rose", border: "border-rose-300", bg: "bg-rose-50", text: "text-rose-700" },
];

function ratingFromScore(total) {
  const t = Number(total) || 0;
  return RATING_MATRIX.find((r) => t >= r.min) || RATING_MATRIX[RATING_MATRIX.length - 1];
}

function RatingBadge({ score, size = "sm", showLabel = false }) {
  if (score == null || isNaN(score)) return null;
  const r = ratingFromScore(score);
  const padding = size === "lg" ? "px-3 py-1.5 text-sm" : "px-2 py-0.5 text-xs";
  return (
    <span data-testid={`rating-${r.code}`}
      className={`inline-flex items-center rounded-full border font-semibold ${r.border} ${r.bg} ${r.text} ${padding}`}
      title={r.label}>
      {r.code}{showLabel && <span className="ml-1.5 font-normal opacity-80">· {r.label}</span>}
    </span>
  );
}

function ScoreCard({ label, value, max, color = "blue" }) {
  const colorMap = {
    blue: "from-blue-50 to-blue-100 text-blue-700 border-blue-200",
    emerald: "from-emerald-50 to-emerald-100 text-emerald-700 border-emerald-200",
    amber: "from-amber-50 to-amber-100 text-amber-700 border-amber-200",
    rose: "from-rose-50 to-rose-100 text-rose-700 border-rose-200",
    violet: "from-violet-50 to-violet-100 text-violet-700 border-violet-200",
  };
  return (
    <div className={`rounded-xl border bg-gradient-to-br ${colorMap[color]} p-4`} data-testid={`score-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className="text-xs font-medium uppercase tracking-wide opacity-80">{label}</div>
      <div className="mt-1 flex items-baseline gap-1">
        <span className="text-2xl font-bold">{Number(value).toFixed(2)}</span>
        <span className="text-sm opacity-70">/ {max}</span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Employee: Submit production + view own
// ─────────────────────────────────────────────────────────────────────────
function EmployeeView({ user }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [myQuality, setMyQuality] = useState([]);
  const [refresh, setRefresh] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    date: todayStr(), project: "", client: "", process: "",
    target: 0, achieved: 0, productivity_pct: 0,
    working_hours: 0, ot_hours: 0, remarks: "",
  });
  const [targetInfo, setTargetInfo] = useState(null);  // { target, exists }

  // Auto-fetch target whenever project + process selected
  useEffect(() => {
    if (form.project && form.process) {
      api.get(`/productions/targets/lookup?project=${encodeURIComponent(form.project)}&process=${encodeURIComponent(form.process)}`)
        .then((r) => {
          const t = r.data?.target || 0;
          setTargetInfo({ target: t, exists: r.data?.exists !== false });
          setForm((f) => ({ ...f, target: t }));
        })
        .catch(() => setTargetInfo(null));
    } else {
      setTargetInfo(null);
    }
  }, [form.project, form.process]);

  useEffect(() => {
    api.get("/productions")
      .then((r) => setRows(r.data || []))
      .catch((e) => toast.error(formatApiError(e)));
  }, [refresh]);

  useEffect(() => {
    api.get(`/productions/summary/me?month=${monthStr()}`)
      .then((r) => setSummary(r.data))
      .catch(() => {});
    api.get(`/productions/quality-entries/me?month=${monthStr()}`)
      .then((r) => setMyQuality(r.data || []))
      .catch(() => {});
  }, [refresh]);

  const reset = () => {
    setForm({ date: todayStr(), project: "", client: "", process: "", target: 0, achieved: 0, productivity_pct: 0, working_hours: 0, ot_hours: 0, remarks: "" });
    setEditingId(null);
  };

  const productivity = useMemo(() => {
    const t = Number(form.target || 0), a = Number(form.achieved || 0);
    return t > 0 ? Math.round((a / t) * 100) : 0;
  }, [form.target, form.achieved]);

  const submit = async (asDraft = false) => {
    if (!form.project) return toast.error("Project is required");
    setSubmitting(true);
    try {
      const payload = { ...form, productivity_pct: productivity, submit: !asDraft };
      if (editingId) await api.put(`/productions/${editingId}`, payload);
      else await api.post("/productions", payload);
      toast.success(asDraft ? "Saved as draft" : "Submitted for review");
      setOpen(false); reset(); setRefresh((v) => v + 1);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const editRow = (r) => {
    setForm({
      date: r.date, project: r.project, client: r.client || "", process: r.process || "",
      target: r.target || 0, achieved: r.achieved || 0, productivity_pct: r.productivity_pct || 0,
      working_hours: r.working_hours || 0, ot_hours: r.ot_hours || 0, remarks: r.remarks || "",
    });
    setEditingId(r.id);
    setOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Scorecard summary */}
      {summary && (
        <Card className="p-6">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500">My Monthly Scorecard</div>
              <div className="flex items-center gap-3">
                <div className="text-2xl font-bold text-slate-900">{summary.averages.total_score.toFixed(2)} <span className="text-base font-normal text-slate-500">/ 10</span></div>
                {summary.approved_records > 0 && <RatingBadge score={summary.averages.total_score} size="lg" showLabel />}
              </div>
            </div>
            <div className="text-xs text-slate-500 text-right">
              <div>{summary.month}</div>
              <div>{summary.approved_records} approved · {summary.pending_records} pending · {summary.rejected_records} rejected</div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <ScoreCard label="Production" value={summary.averages.production_score} max={MAX.production_score} color="blue" />
            <ScoreCard label="Quality" value={summary.averages.quality_score} max={MAX.quality_score} color="emerald" />
            <ScoreCard label="Attendance" value={summary.averages.attendance_score} max={MAX.attendance_score} color="amber" />
            <ScoreCard label="Discipline" value={summary.averages.discipline_score} max={MAX.discipline_score} color="rose" />
            <ScoreCard label="Assessment" value={summary.averages.assessment_score} max={MAX.assessment_score} color="violet" />
          </div>
          {/* Rating Matrix legend */}
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="text-xs uppercase text-slate-500 mb-2">Rating Scale</div>
            <div className="flex flex-wrap gap-2">
              {RATING_MATRIX.slice().reverse().map((r) => (
                <div key={r.code} className={`text-xs rounded-md border ${r.border} ${r.bg} ${r.text} px-2 py-1`}>
                  <b>{r.code}</b> <span className="font-mono">{r.code === "NI" ? "< 9.25" : r.code === "CME" ? "9.25–9.49" : r.code === "AE" ? "9.50–9.74" : "9.75–10.00"}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-slate-900">My Production History</h3>
        <Button data-testid="add-production-btn" onClick={() => { reset(); setOpen(true); }} className="bg-[#2563EB] hover:bg-[#1d4ed8]">
          <Plus className="h-4 w-4 mr-1.5" /> New Production Entry
        </Button>
      </div>

      {myQuality.length > 0 && (
        <Card className="overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <div className="text-sm font-semibold text-slate-900">My Quality Score · {monthStr()}</div>
            <div className="text-xs text-slate-500">Recorded by your Team Lead. Score /4 = (Coding % + OASIS %) ÷ 2 × 4 ÷ 100.</div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 text-xs uppercase">
              <tr>
                <th className="px-4 py-2.5 text-left">Date</th>
                <th className="px-4 py-2.5 text-right">Coding %</th>
                <th className="px-4 py-2.5 text-right">OASIS %</th>
                <th className="px-4 py-2.5 text-right">Overall %</th>
                <th className="px-4 py-2.5 text-right">Quality /4</th>
              </tr>
            </thead>
            <tbody>
              {myQuality.map((q) => (
                <tr key={q.date} className="border-b border-slate-100">
                  <td className="px-4 py-2.5">{q.date}</td>
                  <td className="px-4 py-2.5 text-right font-mono">{q.coding_score}</td>
                  <td className="px-4 py-2.5 text-right font-mono">{q.oasis_score}</td>
                  <td className="px-4 py-2.5 text-right font-mono">{(q.overall_pct ?? 0).toFixed(2)}%</td>
                  <td className="px-4 py-2.5 text-right font-mono font-medium text-emerald-700">{(q.quality_score ?? 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 text-xs uppercase">
            <tr>
              <th className="px-4 py-2.5 text-left">Date</th>
              <th className="px-4 py-2.5 text-left">Project</th>
              <th className="px-4 py-2.5 text-left">Process</th>
              <th className="px-4 py-2.5 text-right">Target/Achieved</th>
              <th className="px-4 py-2.5 text-right">Total /10</th>
              <th className="px-4 py-2.5 text-left">Rating</th>
              <th className="px-4 py-2.5 text-left">Status</th>
              <th className="px-4 py-2.5 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">No entries yet. Click &quot;New Production Entry&quot; to start.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`production-row-${r.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2.5">{r.date}</td>
                <td className="px-4 py-2.5">{r.project}</td>
                <td className="px-4 py-2.5">{r.process || "—"}</td>
                <td className="px-4 py-2.5 text-right font-mono text-xs">{r.target} / {r.achieved}</td>
                <td className="px-4 py-2.5 text-right font-mono font-medium">{r.status === "approved" ? r.total_score?.toFixed(2) : "—"}</td>
                <td className="px-4 py-2.5">{r.status === "approved" ? <RatingBadge score={r.total_score} /> : <span className="text-slate-400 text-xs">—</span>}</td>
                <td className="px-4 py-2.5"><StatusBadge status={r.status} /></td>
                <td className="px-4 py-2.5 text-right">
                  {(r.status === "draft" || r.status === "rejected") && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => editRow(r)} data-testid={`edit-production-${r.id}`}>Edit</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit" : "New"} Production Entry</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date *</Label><Input data-testid="prod-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            <div>
              <Label>Project *</Label>
              <Select value={form.project} onValueChange={(v) => setForm({ ...form, project: v })}>
                <SelectTrigger data-testid="prod-project"><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent>{PROJECT_OPTIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Client</Label>
              <Select value={form.client} onValueChange={(v) => setForm({ ...form, client: v })}>
                <SelectTrigger data-testid="prod-client"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>{CLIENT_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Process</Label>
              <Select value={form.process} onValueChange={(v) => setForm({ ...form, process: v })}>
                <SelectTrigger data-testid="prod-process"><SelectValue placeholder="Select process" /></SelectTrigger>
                <SelectContent>{PROCESS_OPTIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Target {targetInfo && !targetInfo.exists && <span className="text-rose-600 text-xs font-normal">· Not yet assigned by TL</span>}</Label>
              <Input data-testid="prod-target" type="number" value={form.target} disabled className="bg-slate-50 cursor-not-allowed" />
            </div>
            <div><Label>Achieved</Label><Input data-testid="prod-achieved" type="number" min={0} value={form.achieved} onChange={(e) => setForm({ ...form, achieved: e.target.value })} /></div>
            <div><Label>Productivity %</Label><Input value={productivity} disabled className="bg-slate-50" /></div>
            <div><Label>Working Hours</Label><Input data-testid="prod-hours" type="number" min={0} step="0.5" value={form.working_hours} onChange={(e) => setForm({ ...form, working_hours: e.target.value })} /></div>
            <div><Label>OT Hours</Label><Input data-testid="prod-ot" type="number" min={0} step="0.5" value={form.ot_hours} onChange={(e) => setForm({ ...form, ot_hours: e.target.value })} /></div>
            <div className="col-span-2"><Label>Remarks</Label><Textarea data-testid="prod-remarks" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} rows={2} /></div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" disabled={submitting} onClick={() => submit(true)} data-testid="save-draft-btn">Save as Draft</Button>
            <Button disabled={submitting} onClick={() => submit(false)} className="bg-[#2563EB] hover:bg-[#1d4ed8]" data-testid="submit-production-btn">
              {submitting && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}Submit for Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Team Lead view: pending queue + evaluation form + team summary
// ─────────────────────────────────────────────────────────────────────────
function TLView() {
  const [tab, setTab] = useState("pending");
  const [pending, setPending] = useState([]);
  const [teamSummary, setTeamSummary] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [activeRow, setActiveRow] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectOpen, setRejectOpen] = useState(false);

  useEffect(() => {
    api.get("/productions?status=pending_tl").then((r) => setPending(r.data || []));
  }, [refresh]);

  useEffect(() => {
    api.get(`/productions/summary/team?month=${monthStr()}`).then((r) => setTeamSummary(r.data));
  }, [refresh]);

  // Clear stale selections when pending list changes
  useEffect(() => {
    setSelected((prev) => {
      const ids = new Set(pending.map((p) => p.id));
      const next = new Set();
      prev.forEach((id) => { if (ids.has(id)) next.add(id); });
      return next;
    });
  }, [pending]);

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => {
      if (prev.size === pending.length) return new Set();
      return new Set(pending.map((p) => p.id));
    });
  };

  const bulkApprove = async () => {
    if (selected.size === 0) return toast.error("Select at least one record");
    setSubmitting(true);
    try {
      const r = await api.post("/productions/bulk-tl-approve", {
        prod_ids: Array.from(selected),
        comments: "Bulk approved",
      });
      const skipped = r.data?.skipped?.length || 0;
      toast.success(`Approved ${r.data?.approved || 0}/${r.data?.total || 0}${skipped ? ` · ${skipped} skipped` : ""}`);
      setSelected(new Set());
      setRefresh((v) => v + 1);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSubmitting(false); }
  };

  const submitReject = async () => {
    if (!rejectReason.trim()) return toast.error("Reason required");
    try {
      await api.post(`/productions/${activeRow.id}/tl-reject`, { reason: rejectReason });
      toast.success("Returned to employee for correction");
      setRejectOpen(false); setActiveRow(null); setRejectReason("");
      setRefresh((v) => v + 1);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Tabs value={tab} onValueChange={setTab}>
      <TabsList>
        <TabsTrigger value="pending" data-testid="tab-pending"><ClipboardCheck className="h-4 w-4 mr-1.5" />Pending ({pending.length})</TabsTrigger>
        <TabsTrigger value="team" data-testid="tab-team"><TrendingUp className="h-4 w-4 mr-1.5" />Team Summary</TabsTrigger>
        <TabsTrigger value="targets" data-testid="tab-targets"><TargetIcon className="h-4 w-4 mr-1.5" />Targets &amp; Scores</TabsTrigger>
      </TabsList>

      <TabsContent value="pending" className="mt-4">
        <Card className="overflow-hidden">
          <div className="p-3 border-b border-slate-100 flex items-center justify-between bg-slate-50/60">
            <div className="text-xs text-slate-600">
              {selected.size > 0 ? <span><b>{selected.size}</b> selected</span> : <span>Select records to approve. Scores auto-compute from weekly targets, quality entries & monthly scorecards.</span>}
            </div>
            <Button
              size="sm"
              data-testid="bulk-approve-tl-btn"
              disabled={submitting || selected.size === 0}
              onClick={bulkApprove}
              className="h-8 bg-[#16A34A] hover:bg-[#15803d] disabled:opacity-50"
            >
              {submitting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
              Bulk Approve ({selected.size})
            </Button>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 text-xs uppercase">
              <tr>
                <th className="px-4 py-2.5 text-left w-10">
                  <Checkbox
                    data-testid="select-all-tl"
                    checked={pending.length > 0 && selected.size === pending.length}
                    onCheckedChange={toggleAll}
                    aria-label="Select all"
                  />
                </th>
                <th className="px-4 py-2.5 text-left">Date</th>
                <th className="px-4 py-2.5 text-left">Employee</th>
                <th className="px-4 py-2.5 text-left">Project / Process</th>
                <th className="px-4 py-2.5 text-right">Target/Achieved</th>
                <th className="px-4 py-2.5 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {pending.length === 0 && (<tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No pending evaluations</td></tr>)}
              {pending.map((r) => (
                <tr key={r.id} className={`border-b border-slate-100 ${selected.has(r.id) ? "bg-emerald-50/40" : ""}`}>
                  <td className="px-4 py-2.5">
                    <Checkbox
                      data-testid={`select-tl-${r.id}`}
                      checked={selected.has(r.id)}
                      onCheckedChange={() => toggleOne(r.id)}
                      aria-label={`Select ${r.employee_name}`}
                    />
                  </td>
                  <td className="px-4 py-2.5">{r.date}</td>
                  <td className="px-4 py-2.5"><div className="font-medium">{r.employee_name}</div><div className="text-xs text-slate-500">{r.employee_id}</div></td>
                  <td className="px-4 py-2.5">{r.project}<div className="text-xs text-slate-500">{r.process || ""}</div></td>
                  <td className="px-4 py-2.5 text-right font-mono text-xs">{r.target} / {r.achieved}</td>
                  <td className="px-4 py-2.5 text-right">
                    <Button size="sm" variant="outline" className="h-7 text-xs text-rose-600 border-rose-200 hover:bg-rose-50"
                      data-testid={`reject-btn-${r.id}`}
                      onClick={() => { setActiveRow(r); setRejectOpen(true); }}>
                      <XCircle className="h-3.5 w-3.5 mr-1" />Reject
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </TabsContent>

      <TabsContent value="team" className="mt-4">
        {teamSummary && (
          <Card className="overflow-hidden">
            <div className="p-4 border-b border-slate-100 text-xs text-slate-500">Month: {teamSummary.month} · {teamSummary.total_records} records</div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 text-xs uppercase">
                <tr>
                  <th className="px-4 py-2.5 text-left">Employee</th>
                  <th className="px-4 py-2.5 text-right">Approved</th>
                  <th className="px-4 py-2.5 text-right">Pending</th>
                  <th className="px-4 py-2.5 text-right">Rejected</th>
                  <th className="px-4 py-2.5 text-right">Avg Score /10</th>
                  <th className="px-4 py-2.5 text-left">Rating</th>
                </tr>
              </thead>
              <tbody>
                {teamSummary.rows.length === 0 && (<tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No team data this month</td></tr>)}
                {teamSummary.rows.map((row) => (
                  <tr key={row.employee_id} className="border-b border-slate-100">
                    <td className="px-4 py-2.5"><div className="font-medium">{row.employee_name}</div><div className="text-xs text-slate-500">{row.employee_id}</div></td>
                    <td className="px-4 py-2.5 text-right">{row.approved}</td>
                    <td className="px-4 py-2.5 text-right">{row.pending}</td>
                    <td className="px-4 py-2.5 text-right">{row.rejected}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-medium">{row.avg_score.toFixed(2)}</td>
                    <td className="px-4 py-2.5">{row.count_approved > 0 ? <RatingBadge score={row.avg_score} /> : <span className="text-slate-400 text-xs">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </TabsContent>

      <TabsContent value="targets" className="mt-4">
        <TargetsAndScoresPanel />
      </TabsContent>

      {/* Reject dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject & Return to Employee · {activeRow?.employee_name}</DialogTitle></DialogHeader>
          <Textarea rows={3} placeholder="Reason for rejection" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} data-testid="reject-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button onClick={submitReject} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-reject-btn">Confirm Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Tabs>
  );
}

function ScoreField({ label, max, value, onChange, step = 0.1, testId }) {
  return (
    <div className="grid grid-cols-3 gap-3 items-center">
      <Label className="text-sm font-medium">{label} <span className="text-xs text-slate-500">(/{max})</span></Label>
      <div className="col-span-2">
        <Input
          data-testid={testId}
          type="number"
          min={0}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// TL/Mgr: Targets & Scores Panel — per-employee daily target + monthly Q/D/A
// ─────────────────────────────────────────────────────────────────────────
function TargetsAndScoresPanel() {
  const [team, setTeam] = useState([]);
  const [scorecards, setScorecards] = useState([]);
  const [month, setMonth] = useState(monthStr());
  const [refresh, setRefresh] = useState(0);
  const [draft, setDraft] = useState({});

  // Weekly Quality state — one entry per (employee, week)
  const [qWeekStart, setQWeekStart] = useState(() => {
    const d = new Date(); const day = d.getDay();
    d.setDate(d.getDate() - day + (day === 0 ? -6 : 1));
    return d.toISOString().slice(0, 10);
  });
  const [qWeekEnd, setQWeekEnd] = useState(() => {
    const d = new Date(); const day = d.getDay();
    d.setDate(d.getDate() - day + (day === 0 ? -6 : 1) + 6);
    return d.toISOString().slice(0, 10);
  });
  const [qEntries, setQEntries] = useState({});  // by employee_id

  useEffect(() => {
    api.get("/my-team").then((r) => setTeam(r.data?.direct || []));
  }, []);

  useEffect(() => {
    api.get(`/productions/scorecards/list?month=${month}`).then((r) => setScorecards(r.data || []));
  }, [month, refresh]);

  useEffect(() => {
    // Use week_start as the canonical date slot for this employee's weekly quality
    api.get(`/productions/quality-entries/list?date=${qWeekStart}`).then((r) => {
      const map = {};
      (r.data || []).forEach((e) => { map[e.employee_id] = e; });
      setQEntries(map);
    });
  }, [qWeekStart, refresh]);

  const scorecardByEmp = useMemo(() => {
    const m = {};
    scorecards.forEach((s) => { m[s.employee_id] = s; });
    return m;
  }, [scorecards]);

  const setRowField = (eid, field, value) => {
    setDraft((d) => ({ ...d, [eid]: { ...(d[eid] || {}), [field]: value } }));
  };

  const saveScorecard = async (eid) => {
    const r = draft[eid] || {};
    const existing = scorecardByEmp[eid] || {};
    const payload = {
      employee_id: eid,
      month,
      quality_score: parseFloat(r.quality_score ?? existing.quality_score ?? 0),
      discipline_score: parseFloat(r.discipline_score ?? existing.discipline_score ?? 0),
      assessment_score: parseFloat(r.assessment_score ?? existing.assessment_score ?? 0),
      notes: r.notes ?? existing.notes ?? "",
    };
    try {
      await api.post("/productions/scorecards", payload);
      toast.success(`Scores saved for ${month}`);
      setRefresh((v) => v + 1);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveQuality = async (eid) => {
    const r = draft[eid] || {};
    const existing = qEntries[eid] || {};
    const payload = {
      employee_id: eid,
      date: qWeekStart,
      coding_score: parseFloat(r.q_coding ?? existing.coding_score ?? 0),
      oasis_score: parseFloat(r.q_oasis ?? existing.oasis_score ?? 0),
      rework: r.q_rework ?? existing.rework ?? "",
      notes: r.q_notes ?? existing.notes ?? "",
    };
    try {
      await api.post("/productions/quality-entries", payload);
      toast.success(`Weekly quality saved · ${qWeekStart} → ${qWeekEnd}`);
      setRefresh((v) => v + 1);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteQuality = async (eid) => {
    if (!window.confirm(`Delete weekly quality for ${qWeekStart} → ${qWeekEnd}?`)) return;
    try {
      await api.delete(`/productions/quality-entries/${eid}__${qWeekStart}`);
      toast.success("Deleted");
      setRefresh((v) => v + 1);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6">
      {/* Monthly Scorecards */}
      <Card className="overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-slate-900">Monthly Quality · Discipline · Assessment Scores</div>
            <div className="text-xs text-slate-500">These pre-fill each production evaluation for the selected month.</div>
          </div>
          <Input type="month" className="w-40" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="scorecard-month" />
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 text-xs uppercase">
            <tr>
              <th className="px-4 py-2.5 text-left">Employee</th>
              <th className="px-4 py-2.5 text-right">Quality (/4)</th>
              <th className="px-4 py-2.5 text-right">Discipline (/0.5)</th>
              <th className="px-4 py-2.5 text-right">Assessment (/1)</th>
              <th className="px-4 py-2.5 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {team.length === 0 && (<tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No team members.</td></tr>)}
            {team.map((emp) => {
              const sc = scorecardByEmp[emp.employee_id] || {};
              const d = draft[emp.employee_id] || {};
              return (
                <tr key={emp.employee_id} className="border-b border-slate-100">
                  <td className="px-4 py-2.5">
                    <div className="font-medium">{emp.name}</div>
                    <div className="text-xs text-slate-500">{emp.employee_id}</div>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <Input type="number" min={0} max={4} step={0.1} className="h-8 w-20 ml-auto" data-testid={`sc-q-${emp.employee_id}`}
                      defaultValue={sc.quality_score ?? 0}
                      onChange={(e) => setRowField(emp.employee_id, "quality_score", e.target.value)} />
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <Input type="number" min={0} max={0.5} step={0.05} className="h-8 w-20 ml-auto" data-testid={`sc-d-${emp.employee_id}`}
                      defaultValue={sc.discipline_score ?? 0}
                      onChange={(e) => setRowField(emp.employee_id, "discipline_score", e.target.value)} />
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <Input type="number" min={0} max={1} step={0.05} className="h-8 w-20 ml-auto" data-testid={`sc-a-${emp.employee_id}`}
                      defaultValue={sc.assessment_score ?? 0}
                      onChange={(e) => setRowField(emp.employee_id, "assessment_score", e.target.value)} />
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <Button size="sm" className="h-7 text-xs bg-[#16A34A] hover:bg-[#15803d]" data-testid={`save-scorecard-${emp.employee_id}`} onClick={() => saveScorecard(emp.employee_id)}>Save</Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {/* Weekly Quality entry */}
      <Card className="overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-sm font-semibold text-slate-900">Weekly Quality Entry</div>
            <div className="text-xs text-slate-500">One entry per employee for the selected week. Coding % &amp; OASIS % auto-compute Overall % and Quality /4.</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1">
              <Label className="text-xs whitespace-nowrap">Week Start</Label>
              <Input type="date" className="w-40" value={qWeekStart} onChange={(e) => setQWeekStart(e.target.value)} data-testid="quality-week-start" />
            </div>
            <div className="flex items-center gap-1">
              <Label className="text-xs whitespace-nowrap">Week End</Label>
              <Input type="date" className="w-40" value={qWeekEnd} onChange={(e) => setQWeekEnd(e.target.value)} data-testid="quality-week-end" />
            </div>
            <label className="inline-flex">
              <input type="file" accept=".csv,.xlsx,.xls" className="hidden" data-testid="import-quality-input" onChange={async (e) => {
                const f = e.target.files?.[0]; if (!f) return;
                const fd = new FormData(); fd.append("file", f);
                try {
                  const r = await api.post("/productions/quality-entries/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
                  toast.success(`Imported ${r.data.imported}/${r.data.total_rows} rows${r.data.skipped.length ? ` · ${r.data.skipped.length} skipped` : ""}`);
                  setRefresh((v) => v + 1);
                } catch (err) { toast.error(formatApiError(err)); }
                e.target.value = "";
              }} />
              <Button asChild size="sm" data-testid="import-quality-btn" className="bg-[#2563EB] hover:bg-[#1d4ed8] cursor-pointer">
                <span><FileText className="h-3.5 w-3.5 mr-1.5" />Import</span>
              </Button>
            </label>
            {["csv", "xlsx", "pdf"].map((f) => (
              <Button key={f} size="sm" variant="outline" data-testid={`export-quality-${f}`} onClick={() => {
                const token = localStorage.getItem("hr_token");
                window.open(`${process.env.REACT_APP_BACKEND_URL}/api/productions/quality-entries/export?from_date=${qWeekStart}&to_date=${qWeekEnd}&fmt=${f}&auth=${token}`, "_blank");
              }}>
                {f.toUpperCase()}
              </Button>
            ))}
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 text-xs uppercase">
            <tr>
              <th className="px-4 py-2.5 text-left">Employee</th>
              <th className="px-4 py-2.5 text-right">Coding %</th>
              <th className="px-4 py-2.5 text-right">OASIS %</th>
              <th className="px-4 py-2.5 text-right">Overall %</th>
              <th className="px-4 py-2.5 text-right">Quality /4</th>
              <th className="px-4 py-2.5 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {team.length === 0 && (<tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No team members.</td></tr>)}
            {team.map((emp) => {
              const e = qEntries[emp.employee_id] || {};
              const d = draft[emp.employee_id] || {};
              const c = parseFloat(d.q_coding ?? e.coding_score ?? 0) || 0;
              const o = parseFloat(d.q_oasis ?? e.oasis_score ?? 0) || 0;
              const overall = (c + o) / 2;
              const qs = Math.round((overall / 100) * 4 * 100) / 100;
              return (
                <tr key={emp.employee_id} className="border-b border-slate-100">
                  <td className="px-4 py-2.5">
                    <div className="font-medium">{emp.name}</div>
                    <div className="text-xs text-slate-500">{emp.employee_id}</div>
                  </td>
                  <td className="px-4 py-2.5 text-right"><Input type="number" min={0} max={100} step={0.1} className="h-8 w-20 ml-auto" data-testid={`qd-coding-${emp.employee_id}`} defaultValue={e.coding_score ?? 0} onChange={(ev) => setRowField(emp.employee_id, "q_coding", ev.target.value)} /></td>
                  <td className="px-4 py-2.5 text-right"><Input type="number" min={0} max={100} step={0.1} className="h-8 w-20 ml-auto" data-testid={`qd-oasis-${emp.employee_id}`} defaultValue={e.oasis_score ?? 0} onChange={(ev) => setRowField(emp.employee_id, "q_oasis", ev.target.value)} /></td>
                  <td className="px-4 py-2.5 text-right font-mono text-slate-700">{overall.toFixed(2)}%</td>
                  <td className="px-4 py-2.5 text-right font-mono font-medium text-emerald-700">{qs.toFixed(2)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" data-testid={`save-quality-${emp.employee_id}`} onClick={() => saveQuality(emp.employee_id)}>Save</Button>
                      {e.coding_score !== undefined && (
                        <Button size="sm" variant="outline" className="h-7 text-xs text-rose-600" data-testid={`delete-quality-${emp.employee_id}`} onClick={() => deleteQuality(emp.employee_id)}>Delete</Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {/* Bulk dialog removed — replaced by CSV Import/Export above */}
      <WeeklyTargetsCard team={team} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// TL: Weekly Targets card with Import + CSV/XLSX/PDF export + report table
// ─────────────────────────────────────────────────────────────────────────
function WeeklyTargetsCard({ team }) {
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date(); const day = d.getDay();
    d.setDate(d.getDate() - day + (day === 0 ? -6 : 1));
    return d.toISOString().slice(0, 10);
  });
  const [weekEnd, setWeekEnd] = useState(() => {
    const d = new Date(); const day = d.getDay();
    d.setDate(d.getDate() - day + (day === 0 ? -6 : 1) + 6);
    return d.toISOString().slice(0, 10);
  });
  const [rows, setRows] = useState([]);
  const [refresh, setRefresh] = useState(0);
  const [drafts, setDrafts] = useState({});

  useEffect(() => {
    api.get(`/productions/weekly-targets/report?week_start=${weekStart}&week_end=${weekEnd}`)
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]));
  }, [weekStart, weekEnd, refresh]);

  const byEmp = useMemo(() => {
    const m = {}; rows.forEach((r) => { m[r.employee_id] = r; }); return m;
  }, [rows]);

  const saveOne = async (eid) => {
    const v = drafts[eid];
    if (!v) return;
    try {
      await api.post("/productions/weekly-targets", {
        employee_id: eid, week_start: weekStart, week_end: weekEnd, target_charts: parseFloat(v),
      });
      toast.success("Weekly target saved");
      setRefresh((x) => x + 1);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Card className="overflow-hidden">
      <div className="p-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-sm font-semibold text-slate-900">Weekly Production Targets</div>
          <div className="text-xs text-slate-500">Week {weekStart} → {weekEnd}. Achievement % &amp; Production Score auto-computed from daily submissions.</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1">
            <Label className="text-xs whitespace-nowrap">Week Start</Label>
            <Input type="date" className="w-40" value={weekStart} onChange={(e) => setWeekStart(e.target.value)} data-testid="weekly-start-date" />
          </div>
          <div className="flex items-center gap-1">
            <Label className="text-xs whitespace-nowrap">Week End</Label>
            <Input type="date" className="w-40" value={weekEnd} onChange={(e) => setWeekEnd(e.target.value)} data-testid="weekly-end-date" />
          </div>
          <label className="inline-flex">
            <input type="file" accept=".csv,.xlsx,.xls" className="hidden" data-testid="import-weekly-input" onChange={async (e) => {
              const f = e.target.files?.[0]; if (!f) return;
              const fd = new FormData(); fd.append("file", f);
              try {
                const r = await api.post("/productions/weekly-targets/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
                toast.success(`Imported ${r.data.imported}/${r.data.total_rows} rows${r.data.skipped.length ? ` · ${r.data.skipped.length} skipped` : ""}`);
                setRefresh((x) => x + 1);
              } catch (err) { toast.error(formatApiError(err)); }
              e.target.value = "";
            }} />
            <Button asChild size="sm" data-testid="import-weekly-btn" className="bg-[#2563EB] hover:bg-[#1d4ed8] cursor-pointer">
              <span><FileText className="h-3.5 w-3.5 mr-1.5" />Import</span>
            </Button>
          </label>
          {["csv", "xlsx", "pdf"].map((f) => (
            <Button key={f} size="sm" variant="outline" data-testid={`export-weekly-${f}`} onClick={() => {
              const token = localStorage.getItem("hr_token");
              window.open(`${process.env.REACT_APP_BACKEND_URL}/api/productions/weekly-targets/export?week_start=${weekStart}&week_end=${weekEnd}&fmt=${f}&auth=${token}`, "_blank");
            }}>
              {f.toUpperCase()}
            </Button>
          ))}
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 text-xs uppercase">
          <tr>
            <th className="px-4 py-2.5 text-left">Employee</th>
            <th className="px-4 py-2.5 text-right">Target</th>
            <th className="px-4 py-2.5 text-right">Actual</th>
            <th className="px-4 py-2.5 text-right">Achievement %</th>
            <th className="px-4 py-2.5 text-right">Production /4</th>
            <th className="px-4 py-2.5 text-right">Action</th>
          </tr>
        </thead>
        <tbody>
          {team.length === 0 && (<tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No team members.</td></tr>)}
          {team.map((emp) => {
            const r = byEmp[emp.employee_id];
            return (
              <tr key={emp.employee_id} className="border-b border-slate-100">
                <td className="px-4 py-2.5">
                  <div className="font-medium">{emp.name}</div>
                  <div className="text-xs text-slate-500">{emp.employee_id}</div>
                </td>
                <td className="px-4 py-2.5 text-right">
                  <Input type="number" min={0} className="h-8 w-24 ml-auto" data-testid={`wt-${emp.employee_id}`}
                    defaultValue={r?.target_charts ?? 0}
                    onChange={(e) => setDrafts({ ...drafts, [emp.employee_id]: e.target.value })} />
                </td>
                <td className="px-4 py-2.5 text-right font-mono">{r?.actual_charts ?? 0}</td>
                <td className="px-4 py-2.5 text-right font-mono">{r ? `${r.achievement_pct.toFixed(2)}%` : "—"}</td>
                <td className="px-4 py-2.5 text-right font-mono font-medium text-emerald-700">{r ? r.production_score.toFixed(2) : "—"}</td>
                <td className="px-4 py-2.5 text-right">
                  <Button size="sm" className="h-7 text-xs bg-[#2563EB] hover:bg-[#1d4ed8]" data-testid={`save-weekly-${emp.employee_id}`} onClick={() => saveOne(emp.employee_id)}>Save</Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Manager view: pending queue + dept summary
// ─────────────────────────────────────────────────────────────────────────
function ManagerView() {
  const [tab, setTab] = useState("pending");
  const [pending, setPending] = useState([]);
  const [deptSummary, setDeptSummary] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const [activeRow, setActiveRow] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [comments, setComments] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/productions?status=pending_mgr").then((r) => setPending(r.data || []));
  }, [refresh]);

  useEffect(() => {
    api.get(`/productions/summary/team?month=${monthStr()}`).then((r) => setDeptSummary(r.data));
  }, [refresh]);

  useEffect(() => {
    setSelected((prev) => {
      const ids = new Set(pending.map((p) => p.id));
      const next = new Set();
      prev.forEach((id) => { if (ids.has(id)) next.add(id); });
      return next;
    });
  }, [pending]);

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => {
      if (prev.size === pending.length) return new Set();
      return new Set(pending.map((p) => p.id));
    });
  };

  const bulkApprove = async () => {
    if (selected.size === 0) return toast.error("Select at least one record");
    setSubmitting(true);
    try {
      const r = await api.post("/productions/bulk-mgr-approve", {
        prod_ids: Array.from(selected),
        comments: "Bulk approved",
      });
      const skipped = r.data?.skipped?.length || 0;
      toast.success(`Approved ${r.data?.approved || 0}/${r.data?.total || 0}${skipped ? ` · ${skipped} skipped` : ""}`);
      setSelected(new Set());
      setRefresh((v) => v + 1);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSubmitting(false); }
  };

  const decide = useCallback(async (action) => {
    if (action === "mgr-reject" && !comments.trim()) return toast.error("Comment required for rejection");
    try {
      const payload = action === "mgr-approve" ? { comments } : { reason: comments };
      await api.post(`/productions/${activeRow.id}/${action}`, payload);
      toast.success(action === "mgr-approve" ? "Approved" : "Returned to Team Lead");
      setReviewOpen(false); setActiveRow(null); setComments(""); setRefresh((v) => v + 1);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [activeRow, comments]);

  return (
    <Tabs value={tab} onValueChange={setTab}>
      <TabsList>
        <TabsTrigger value="pending" data-testid="tab-mgr-pending"><ShieldCheck className="h-4 w-4 mr-1.5" />Pending Approvals ({pending.length})</TabsTrigger>
        <TabsTrigger value="dept" data-testid="tab-mgr-dept"><TrendingUp className="h-4 w-4 mr-1.5" />Department</TabsTrigger>
      </TabsList>

      <TabsContent value="pending" className="mt-4">
        <Card className="overflow-hidden">
          <div className="p-3 border-b border-slate-100 flex items-center justify-between bg-slate-50/60">
            <div className="text-xs text-slate-600">
              {selected.size > 0 ? <span><b>{selected.size}</b> selected</span> : <span>Select records to lock as approved, or click Review for individual decisions.</span>}
            </div>
            <Button
              size="sm"
              data-testid="bulk-approve-mgr-btn"
              disabled={submitting || selected.size === 0}
              onClick={bulkApprove}
              className="h-8 bg-[#16A34A] hover:bg-[#15803d] disabled:opacity-50"
            >
              {submitting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
              Bulk Approve ({selected.size})
            </Button>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 text-xs uppercase">
              <tr>
                <th className="px-4 py-2.5 text-left w-10">
                  <Checkbox
                    data-testid="select-all-mgr"
                    checked={pending.length > 0 && selected.size === pending.length}
                    onCheckedChange={toggleAll}
                    aria-label="Select all"
                  />
                </th>
                <th className="px-4 py-2.5 text-left">Date</th>
                <th className="px-4 py-2.5 text-left">Employee</th>
                <th className="px-4 py-2.5 text-left">Team Lead</th>
                <th className="px-4 py-2.5 text-right">Total /10</th>
                <th className="px-4 py-2.5 text-left">Rating</th>
                <th className="px-4 py-2.5 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {pending.length === 0 && (<tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No pending approvals</td></tr>)}
              {pending.map((r) => (
                <tr key={r.id} className={`border-b border-slate-100 ${selected.has(r.id) ? "bg-emerald-50/40" : ""}`}>
                  <td className="px-4 py-2.5">
                    <Checkbox
                      data-testid={`select-mgr-${r.id}`}
                      checked={selected.has(r.id)}
                      onCheckedChange={() => toggleOne(r.id)}
                      aria-label={`Select ${r.employee_name}`}
                    />
                  </td>
                  <td className="px-4 py-2.5">{r.date}</td>
                  <td className="px-4 py-2.5"><div className="font-medium">{r.employee_name}</div><div className="text-xs text-slate-500">{r.project}</div></td>
                  <td className="px-4 py-2.5 text-xs">{r.tl_name || "—"}</td>
                  <td className="px-4 py-2.5 text-right font-mono font-medium">{r.total_score?.toFixed(2)}</td>
                  <td className="px-4 py-2.5"><RatingBadge score={r.total_score} /></td>
                  <td className="px-4 py-2.5 text-right">
                    <Button size="sm" variant="outline" data-testid={`review-btn-${r.id}`} onClick={() => { setActiveRow(r); setReviewOpen(true); }} className="h-7 text-xs">Review</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </TabsContent>

      <TabsContent value="dept" className="mt-4">
        {deptSummary && (
          <Card className="overflow-hidden">
            <div className="p-4 border-b border-slate-100 text-xs text-slate-500">Department · {deptSummary.month}</div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 text-xs uppercase">
                <tr>
                  <th className="px-4 py-2.5 text-left">Employee</th>
                  <th className="px-4 py-2.5 text-right">Approved</th>
                  <th className="px-4 py-2.5 text-right">Pending</th>
                  <th className="px-4 py-2.5 text-right">Avg /10</th>
                  <th className="px-4 py-2.5 text-left">Rating</th>
                </tr>
              </thead>
              <tbody>
                {deptSummary.rows.length === 0 && (<tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No data</td></tr>)}
                {deptSummary.rows.map((row) => (
                  <tr key={row.employee_id} className="border-b border-slate-100">
                    <td className="px-4 py-2.5"><div className="font-medium">{row.employee_name}</div><div className="text-xs text-slate-500">{row.employee_id}</div></td>
                    <td className="px-4 py-2.5 text-right">{row.approved}</td>
                    <td className="px-4 py-2.5 text-right">{row.pending}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-medium">{row.avg_score.toFixed(2)}</td>
                    <td className="px-4 py-2.5">{row.count_approved > 0 ? <RatingBadge score={row.avg_score} /> : <span className="text-slate-400 text-xs">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </TabsContent>

      {/* Review Dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Review · {activeRow?.employee_name} · {activeRow?.date}</DialogTitle></DialogHeader>
          {activeRow && (
            <div className="space-y-3">
              <div className="grid grid-cols-5 gap-2">
                <ScoreCard label="Production" value={activeRow.production_score} max={MAX.production_score} color="blue" />
                <ScoreCard label="Quality" value={activeRow.quality_score} max={MAX.quality_score} color="emerald" />
                <ScoreCard label="Attendance" value={activeRow.attendance_score} max={MAX.attendance_score} color="amber" />
                <ScoreCard label="Discipline" value={activeRow.discipline_score} max={MAX.discipline_score} color="rose" />
                <ScoreCard label="Assessment" value={activeRow.assessment_score} max={MAX.assessment_score} color="violet" />
              </div>
              <Card className="p-3 bg-blue-50 border-blue-200 flex items-center justify-between">
                <div className="text-sm text-blue-900">Total</div>
                <div className="flex items-center gap-3">
                  <div className="text-2xl font-bold text-blue-700">{activeRow.total_score?.toFixed(2)} <span className="text-base font-normal">/ 10</span></div>
                  <RatingBadge score={activeRow.total_score} size="lg" showLabel />
                </div>
              </Card>
              {activeRow.tl_comments && <div className="text-xs text-slate-600"><b>TL Comments:</b> {activeRow.tl_comments}</div>}
              <div><Label>Your Comments (required for rejection)</Label><Textarea rows={2} value={comments} onChange={(e) => setComments(e.target.value)} data-testid="mgr-comments" /></div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => decide("mgr-reject")} data-testid="mgr-reject-btn"><XCircle className="h-4 w-4 mr-1.5" />Reject</Button>
            <Button onClick={() => decide("mgr-approve")} className="bg-[#16A34A] hover:bg-[#15803d]" data-testid="mgr-approve-btn"><CheckCircle2 className="h-4 w-4 mr-1.5" />Approve & Lock</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Tabs>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────
export default function Production() {
  const { user } = useAuth();
  const role = user?.role;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Production Performance"
        description="Daily production submissions · Team Lead evaluation · Manager approval (total /10)"
        icon={FileText}
      />

      {role === "employee" && <EmployeeView user={user} />}
      {role === "team_lead" && <TLView />}
      {role === "manager" && <ManagerView />}
      {(role === "hr" || role === "super_admin" || role === "finance") && (
        <Tabs defaultValue="all">
          <TabsList>
            <TabsTrigger value="all" data-testid="tab-hr-all">All Production</TabsTrigger>
            <TabsTrigger value="approvals" data-testid="tab-hr-approvals">Pending Approvals</TabsTrigger>
            <TabsTrigger value="summary" data-testid="tab-hr-summary">Summary</TabsTrigger>
          </TabsList>
          <TabsContent value="all" className="mt-4"><HRAllView /></TabsContent>
          <TabsContent value="approvals" className="mt-4"><TLView /></TabsContent>
          <TabsContent value="summary" className="mt-4"><ManagerView /></TabsContent>
        </Tabs>
      )}
      {!role && (
        <Card className="p-6 text-center text-slate-500"><AlertCircle className="h-6 w-6 mx-auto mb-2" />Loading user context...</Card>
      )}
    </div>
  );
}

function HRAllView() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/productions").then((r) => setRows(r.data || [])); }, []);
  return (
    <Card className="overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 text-xs uppercase">
          <tr>
            <th className="px-4 py-2.5 text-left">Date</th>
            <th className="px-4 py-2.5 text-left">Employee</th>
            <th className="px-4 py-2.5 text-left">Project</th>
            <th className="px-4 py-2.5 text-right">Total /10</th>
            <th className="px-4 py-2.5 text-left">Rating</th>
            <th className="px-4 py-2.5 text-left">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-slate-100">
              <td className="px-4 py-2.5">{r.date}</td>
              <td className="px-4 py-2.5"><div className="font-medium">{r.employee_name}</div><div className="text-xs text-slate-500">{r.employee_id}</div></td>
              <td className="px-4 py-2.5">{r.project}</td>
              <td className="px-4 py-2.5 text-right font-mono font-medium">{r.status === "approved" ? r.total_score?.toFixed(2) : "—"}</td>
              <td className="px-4 py-2.5">{r.status === "approved" ? <RatingBadge score={r.total_score} /> : <span className="text-slate-400 text-xs">—</span>}</td>
              <td className="px-4 py-2.5"><StatusBadge status={r.status} /></td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No records</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}
